// ============================================================================
//  pwa.js — install plumbing
// ============================================================================
//
//  Imported for its side effects at the very top of index.js, BEFORE React
//  renders. That ordering is not cosmetic.
//
//  Chromium fires `beforeinstallprompt` once, early, and if nobody calls
//  preventDefault() on that one event the opportunity is gone for the page
//  load. A React component cannot be relied on to be mounted in time — so the
//  listener is installed at module scope here, the event is parked in this
//  module, and components subscribe to it whenever they get around to it.
//
//  Everything below is capability-detected. Firefox and Safari never fire
//  `beforeinstallprompt` at all; on those, getState().canInstall stays false
//  and the UI shows manual instructions instead of a dead button.

const SNOOZE_KEY  = 'mefims.install.snoozedUntil';
const SNOOZE_DAYS = 7;

// ── Module state ────────────────────────────────────────────────────────────
let deferredPrompt = null;   // the parked BeforeInstallPromptEvent
let installed      = false;
const listeners    = new Set();

const emit = () => { listeners.forEach((fn) => { try { fn(getState()); } catch (_) {} }); };

// ── Environment ─────────────────────────────────────────────────────────────

// Already running as an installed app? Then there is nothing to offer. The
// iOS check is separate because Safari never implemented display-mode.
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
    window.navigator.standalone === true
  );
}

export function isIos() {
  if (typeof navigator === 'undefined') return false;
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );
}

// ── Snooze ──────────────────────────────────────────────────────────────────
// "Not now" must mean not now, not "ask again on the next page load". A
// timestamp rather than a boolean so it is a snooze and not a permanent
// dismissal — someone who declines on a shared workstation in January may
// well want it on their own machine in March.

export function isSnoozed() {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return Number.isFinite(until) && until > Date.now();
  } catch (_) {
    return false;
  }
}

export function snooze(days = SNOOZE_DAYS) {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + days * 86400000));
  } catch (_) { /* private browsing — worst case we ask again */ }
  emit();
}

export function clearSnooze() {
  try { localStorage.removeItem(SNOOZE_KEY); } catch (_) {}
  emit();
}

// ── State + subscription ────────────────────────────────────────────────────

export function getState() {
  return {
    canInstall:  !!deferredPrompt && !installed,
    installed:   installed || isStandalone(),
    standalone:  isStandalone(),
    snoozed:     isSnoozed(),
    // True where we can explain how to install but cannot do it for them:
    // iOS Safari has no programmatic install, only "Add to Home Screen".
    manualOnly:  isIos() && !isStandalone(),
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── The prompt itself ───────────────────────────────────────────────────────
//
//  Returns 'accepted' | 'dismissed' | 'unavailable'.
//
//  The parked event is single-use: once prompt() has been called Chromium will
//  not accept it again, so it is cleared either way. If the user dismissed,
//  the browser will fire a fresh beforeinstallprompt later and we park that
//  one instead.
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';

  const evt = deferredPrompt;
  deferredPrompt = null;
  emit();

  try {
    evt.prompt();
    const { outcome } = await evt.userChoice;
    return outcome === 'accepted' ? 'accepted' : 'dismissed';
  } catch (_) {
    return 'unavailable';
  }
}

// ── Listeners, installed at import time ─────────────────────────────────────

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Without this the browser shows its own mini-infobar and our card never
    // gets a say.
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    // Installed means the question is settled — never ask on this browser
    // again, even if the app is later removed and beforeinstallprompt returns.
    try { localStorage.removeItem(SNOOZE_KEY); } catch (_) {}
    emit();
  });
}

// ── Service worker ──────────────────────────────────────────────────────────
//
//  Registered in production builds only. Two reasons, and the second is the
//  one that bites:
//
//    1. Chromium requires a service worker with a fetch handler before it will
//       fire beforeinstallprompt at all — so in development there is simply no
//       install prompt to test. Test installation against a production build:
//           npm run build && npm run serve
//    2. Registering it under `npm start` is the classic "why are my changes
//       not showing up" trap. Not worth the hour it costs someone.
//
//  Requires a secure context — HTTPS, or localhost. Over plain HTTP on a LAN
//  address the browser refuses to register a worker and the app is not
//  installable. If the facility runs this over http://192.168.x.x, that is the
//  thing to fix.
export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;

  window.addEventListener('load', () => {
    const url = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
    navigator.serviceWorker.register(url).catch((err) => {
      console.warn('[pwa] service worker registration failed:', err && err.message);
    });
  });
}
